import React, { useEffect, useRef, useState } from "react";
import { OtsAircraft } from "../../Models/OtsAircraft";
import "../../theme/styles.css";
import "./EventDetails.scss";
import { getOtsEvent } from "../../services/otsEventService";
import ConversationTimeline from "meconversation/ConversationTimeline";
import { editAircraft } from "../../store/feature/OtsAircraftSlice";
import { refreshEvent } from "../../store/feature/OpenEventsListSlice";
import { useSelector, useDispatch } from 'react-redux'
import "react-multi-carousel/lib/styles.css";
import { useAuthentication } from "meportal/AuthenticationProvider";
import EventDetailsHeader from "./EventDetailsHeader";
import { useLocation } from 'react-router-dom';
import { GlobalTimerProvider } from '../../providers/GlobalTimerProvider';

interface EventDetailsProps {
    open?: boolean;
    loadedData: (data: boolean) => void;
    isHistoryEvent?: boolean;
    conversationFilter?: any;
    resetConversationFilter?: any;
}

export default function EventDetails(props: EventDetailsProps) {
  const { userData, getLatestAccessToken } = useAuthentication();
  const {
    open,
    loadedData,
    isHistoryEvent,
  } = props;

  const [otsEventData, setOtsEventData] = useState<OtsAircraft>();
  const otsEventDataRef = useRef<OtsAircraft>();
  const [loading, setLoader] = useState(true);
  const [error, setError] = useState(false);
  const [conversationReload, setConversationReload] = useState(true);
  const [otsStart, setOtsStart] = useState<string>("");
  const [otsClosedDate, setOtsClosedDate] = useState<string>("");
  const [conversationFilter, setConversationFilter] = useState<any>(null);
  const [eventRefreshOn, setEventRefreshOn] = useState(false);
  const openEventsMasterList = useSelector((state: any) => state.openEvents.openEventsMasterList);
  const aircraft = useSelector((state: any) => state.otsAircraft.otsAircraft);
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => { 
    if(props.conversationFilter){
      setConversationFilter(props.conversationFilter);
      props.resetConversationFilter();
    }
  }, [props.conversationFilter]);

  const refreshTimeline = () => { 
    setConversationReload(!conversationReload);
  }

  async function fetchGetOtsEvent() {
    console.log("Load from state or app refresh?");
    console.log("Event ID: " + aircraft.id + " Conversation ID: " + aircraft.ConversationUniqueKey);
    if (aircraft){
      loadStateData(aircraft);
    }
    loadFetchData(aircraft);
  }

  useEffect(() => {
    let isMounted = true;
    setEventRefreshOn(false);
    fetchGetOtsEvent();
    console.log("EventDetails Here: ", aircraft.ConversationUniqueKey);
    return () => {
      isMounted = false;
    };
  }, [userData, open, aircraft.id]);
  
  const loadStateData = (aircraft: OtsAircraft) => {
    setOtsEventData(aircraft);
    setOtsStart(aircraft.OutOfServiceDateTimeUTC);
    setOtsClosedDate(aircraft.ReturnToServiceTimeUTC ?? "");
    setLoader(false);
    loadedData(true);
  }

  async function loadFetchData(aircraft: OtsAircraft) {
    if (open === false) {
      setConversationReload(!conversationReload);
    }
    if (userData.profile.company) {
      getLatestAccessToken().then( (token: any) => {
        userData.access_token = token;
        getOtsEvent(userData, aircraft.Airline, "N"+ aircraft.AircraftRegistration, aircraft.id).then(
          (otsEventData) => {
            // Check if the event is newer than the current state then only update
            if(otsEventData.LastUpdatedTimeStampUTC >= aircraft.LastUpdatedTimeStampUTC){
              dispatch(editAircraft(otsEventData));

              setOtsEventData(otsEventData);
              setOtsStart(otsEventData.OutOfServiceDateTimeUTC);
              setOtsClosedDate(otsEventData.ReturnToServiceTimeUTC ?? "");
              setLoader(false);
              loadedData(true);
              dispatch(refreshEvent(otsEventData));
              setEventRefreshOn(true);
            }
            else{
              const updatedAircraft = { ...aircraft, isPostClicked: false };
              dispatch(editAircraft(updatedAircraft));
              dispatch(refreshEvent(updatedAircraft));
              setEventRefreshOn(true);
            }
          },
          (error: any) => {
            setLoader(false);
            setError(true);
            const updatedAircraft = { ...aircraft, isPostClicked: false };
            dispatch(editAircraft(updatedAircraft));
            dispatch(refreshEvent(updatedAircraft));
            setEventRefreshOn(true);
          }
        );
      });
    }
  }

  useEffect(() => {
    otsEventDataRef.current = otsEventData;
  }, [otsEventData]);

  //For auto refresh data from the redux (which gets updated from the API every 15-30 seconds)
  useEffect(() => {
    const isOnEventDetailsRoute = location.pathname === '/open-events/event-details';
    const refreshData = async () => {
      if(Array.isArray(openEventsMasterList) && openEventsMasterList.length > 0 && otsEventDataRef.current){
        const otsRefreshedEventData = openEventsMasterList.find(event => event.id === otsEventDataRef.current!.id);
        // If data exists in redux, use it
        if(otsRefreshedEventData && otsRefreshedEventData.InboundFlight){
          if(otsRefreshedEventData.LastUpdatedTimeStampUTC >= otsEventDataRef.current!.LastUpdatedTimeStampUTC){
            setOtsEventData(otsRefreshedEventData);
            dispatch(editAircraft(otsRefreshedEventData));
          }
        }
        else{ //fetch from API if data missing in the redux
          try{
            const token = await getLatestAccessToken();
            userData.access_token = token;
            const responseOtsEventData = await getOtsEvent(userData, otsEventDataRef.current!.Airline, "N"+ otsEventDataRef.current!.AircraftRegistration, otsEventDataRef.current!.id);
            if(responseOtsEventData && responseOtsEventData.LastUpdatedTimeStampUTC >= otsEventDataRef.current!.LastUpdatedTimeStampUTC){
              setOtsEventData(responseOtsEventData);
              dispatch(editAircraft(responseOtsEventData));
            }
          }
          catch(error){
            console.log("Error in fetching data from API", error);
          }
        }
      }
    };
    if(eventRefreshOn && isOnEventDetailsRoute){
      refreshData();
    }
  }, [eventRefreshOn , openEventsMasterList]);

  const renderConversationTimeline = () => {
    if (!otsEventData?.ConversationUniqueKey) {
      return null;
    }

    if (!isHistoryEvent || (isHistoryEvent && eventRefreshOn)) {
      return (
        <ConversationTimeline
          key={conversationReload}
          uniqueKey={otsEventData.ConversationUniqueKey}
          updateTimeline={refreshTimeline}
          hideDelete={!!otsEventData.ReturnToServiceTimeUTC}
          eventTypeId={`${otsEventData.Airline}:${otsEventData.EventType}`}
          ButtonFilter={conversationFilter}
          resetButtonFilter={() => setConversationFilter(null)}
          userData={userData}
          isConversationArchived={!!otsEventData.IsConversationArchived}
        />
      );
    }

    return null;
  };

  return (
    <>
      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div>No valid details for aircraft found.</div>
      ) : (
        <div className={open ? "container-modal-open": isHistoryEvent ? "history-container-details" : "container-details"}>
          <div
            className={open ? "modal-info-container": isHistoryEvent ? "history-info-container" : "info-container"}
          >
            <div>
            <GlobalTimerProvider>
              <EventDetailsHeader otsEventData={otsEventData} isHistoryEvent={isHistoryEvent}></EventDetailsHeader>
            </GlobalTimerProvider>
            </div>
          </div>
        </div>
      )}

      <div className={open ? "container-modal-open details-timeline" : "container-conversation-timeline"}>
        <div className="timeline-container">
          {renderConversationTimeline()}
        </div>
      </div>
    </>
  );
}